import { useState } from "react";
import { X, Send, Phone, Trash2, Building2, Pencil } from "lucide-react";
import { format } from "date-fns";
import type { Lead, LeadStatus, InteractionType } from "../types";
import { VALID_TRANSITIONS, TIER_EMOJI, INTERACTION_TYPES, SIZE_LABELS } from "../constants";
import { LeadStatusBadge } from "./LeadStatusBadge";
import { LeadScoreBadge } from "./LeadScoreBadge";
import { LeadInteractionTimeline } from "./LeadInteractionTimeline";
import { useLocale } from "../../../context/LanguageContext";

interface LeadDetailDrawerProps {
  lead: Lead;
  onClose: () => void;
  onUpdateStatus: (leadId: string, status: LeadStatus, note?: string) => void;
  onAddInteraction: (leadId: string, type: InteractionType, content: string) => void;
  onDelete: (leadId: string) => void;
  onConvert: (lead: Lead) => void;
  onEdit: (lead: Lead) => void;
  loading: boolean;
}

export function LeadDetailDrawer({
  lead,
  onClose,
  onUpdateStatus,
  onAddInteraction,
  onDelete,
  onConvert,
  onEdit,
  loading,
}: LeadDetailDrawerProps) {
  const { t } = useLocale();

  // Status change
  const [showStatusChange, setShowStatusChange] = useState(false);
  const [statusNote, setStatusNote] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<LeadStatus | "">("");

  // Add interaction
  const [showAddInteraction, setShowAddInteraction] = useState(false);
  const [interactionType, setInteractionType] = useState<InteractionType>("NOTE");
  const [interactionContent, setInteractionContent] = useState("");

  const validNext = VALID_TRANSITIONS[lead.status] ?? [];

  const handleStatusSubmit = () => {
    if (!selectedStatus) return;
    onUpdateStatus(lead._id, selectedStatus as LeadStatus, statusNote || undefined);
    setShowStatusChange(false);
    setStatusNote("");
    setSelectedStatus("");
  };

  const handleInteractionSubmit = () => {
    if (!interactionContent.trim()) return;
    onAddInteraction(lead._id, interactionType, interactionContent.trim());
    setShowAddInteraction(false);
    setInteractionContent("");
    setInteractionType("NOTE");
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30" onClick={onClose} />
      <div className="fixed right-0 top-0 z-50 flex h-full w-full max-w-lg flex-col border-l border-border bg-card shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-lg font-semibold text-foreground">
            {t("leads.detail.title")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          <div className="space-y-6">
            {/* Lead Info */}
            <section>
              <h3 className="text-sm font-semibold text-foreground">
                {t("leads.detail.info")}
              </h3>
              <div className="mt-3 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-foreground">{lead.name}</span>
                  <LeadScoreBadge score={lead.qualificationScore} />
                </div>

                <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                  <div>
                    <p className="text-muted-foreground">{t("leads.table.email")}</p>
                    <p className="font-medium text-foreground">{lead.email}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">{t("leads.table.company")}</p>
                    <p className="font-medium text-foreground">{lead.company}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">{t("leads.table.tier")}</p>
                    <p className="font-medium text-foreground">
                      {TIER_EMOJI[lead.tier]} {t(`leads.tier.${lead.tier}`)}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">{t("leads.detail.size")}</p>
                    <p className="font-medium text-foreground">{SIZE_LABELS[lead.size]} {t("leads.form.employees")}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">{t("leads.table.status")}</p>
                    <LeadStatusBadge status={lead.status} />
                  </div>
                  <div>
                    <p className="text-muted-foreground">{t("leads.table.source")}</p>
                    <p className="font-medium text-foreground">{t(`leads.source.${lead.source}`)}</p>
                  </div>
                  {lead.phone && (
                    <div>
                      <p className="text-muted-foreground">{t("leads.detail.phone")}</p>
                      <p className="font-medium text-foreground">
                        <Phone className="mr-1 inline h-3 w-3" />
                        {lead.phone}
                      </p>
                    </div>
                  )}
                  <div>
                    <p className="text-muted-foreground">{t("leads.detail.locale")}</p>
                    <p className="font-medium text-foreground">{lead.locale?.toUpperCase() ?? "EN"}</p>
                  </div>
                </div>

                {/* UTM Data */}
                {(lead.utmSource || lead.utmMedium || lead.utmCampaign) && (
                  <div className="rounded-md bg-muted/50 p-3 text-xs">
                    <p className="font-medium text-muted-foreground">UTM</p>
                    <p className="text-foreground">
                      {[lead.utmSource, lead.utmMedium, lead.utmCampaign]
                        .filter(Boolean)
                        .join(" / ")}
                    </p>
                  </div>
                )}

                {/* Notes */}
                {lead.notes && (
                  <div>
                    <p className="text-sm text-muted-foreground">{t("leads.detail.notes")}</p>
                    <p className="mt-1 text-sm text-foreground">{lead.notes}</p>
                  </div>
                )}

                <p className="text-xs text-muted-foreground">
                  {t("leads.detail.submitted")}: {format(new Date(lead.createdAt), "MMM d, yyyy HH:mm")}
                </p>

                {lead.convertedToOrganizationId && (
                  <div className="rounded-md bg-green-50 p-3 text-xs">
                    <p className="font-medium text-green-800">
                      {t("leads.detail.convertedLabel")}
                    </p>
                    <p className="text-green-700">
                      {t("leads.detail.convertedAt")}: {lead.convertedAt ? format(new Date(lead.convertedAt), "MMM d, yyyy HH:mm") : "—"}
                    </p>
                  </div>
                )}
              </div>
            </section>

            {/* Actions */}
            <section>
              <h3 className="text-sm font-semibold text-foreground">
                {t("leads.detail.actions")}
              </h3>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => onEdit(lead)}
                  disabled={loading}
                  className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted disabled:opacity-50"
                >
                  <Pencil className="mr-1 inline h-3 w-3" />
                  {t("leads.detail.editLead")}
                </button>
                {validNext.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowStatusChange(true)}
                    disabled={loading}
                    className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                  >
                    <Send className="mr-1 inline h-3 w-3" />
                    {t("leads.detail.updateStatus")}
                  </button>
                )}
                {lead.status !== "WON" && lead.status !== "LOST" && !lead.convertedToOrganizationId && (
                  <button
                    type="button"
                    onClick={() => onConvert(lead)}
                    disabled={loading}
                    className="rounded-md bg-green-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
                  >
                    <Building2 className="mr-1 inline h-3 w-3" />
                    {t("leads.detail.convertToOrg")}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => onDelete(lead._id)}
                  disabled={loading}
                  className="rounded-md bg-destructive/10 px-3 py-1.5 text-xs font-medium text-destructive hover:bg-destructive/20 disabled:opacity-50"
                >
                  <Trash2 className="mr-1 inline h-3 w-3" />
                  {t("common.delete")}
                </button>
              </div>

              {/* Status Change Form */}
              {showStatusChange && (
                <div className="mt-3 space-y-2 rounded-md border border-border p-3">
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value as LeadStatus)}
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    <option value="">{t("leads.statusChange.select")}</option>
                    {validNext.map((s) => (
                      <option key={s} value={s}>
                        {t(`leads.status.${s}`)}
                      </option>
                    ))}
                  </select>
                  <textarea
                    value={statusNote}
                    onChange={(e) => setStatusNote(e.target.value)}
                    placeholder={t("leads.statusChange.notePlaceholder")}
                    rows={2}
                    maxLength={2000}
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleStatusSubmit}
                      disabled={!selectedStatus || loading}
                      className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                    >
                      {t("common.confirm")}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowStatusChange(false)}
                      className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted"
                    >
                      {t("common.cancel")}
                    </button>
                  </div>
                </div>
              )}
            </section>

            {/* Interactions */}
            <section>
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-foreground">
                  {t("leads.detail.interactions")}
                </h3>
                <button
                  type="button"
                  onClick={() => setShowAddInteraction(!showAddInteraction)}
                  className="text-xs font-medium text-primary hover:text-primary/80"
                >
                  + {t("leads.detail.addInteraction")}
                </button>
              </div>

              {/* Add Interaction Form */}
              {showAddInteraction && (
                <div className="mt-3 space-y-2 rounded-md border border-border p-3">
                  <select
                    value={interactionType}
                    onChange={(e) => setInteractionType(e.target.value as InteractionType)}
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    {INTERACTION_TYPES.filter((iType) => iType !== "STATUS_CHANGE").map((iType) => (
                      <option key={iType} value={iType}>
                        {t(`leads.interaction.${iType}`)}
                      </option>
                    ))}
                  </select>
                  <textarea
                    value={interactionContent}
                    onChange={(e) => setInteractionContent(e.target.value)}
                    placeholder={t("leads.detail.interactionPlaceholder")}
                    rows={3}
                    maxLength={2000}
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleInteractionSubmit}
                      disabled={!interactionContent.trim() || loading}
                      className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                    >
                      {t("common.submit")}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowAddInteraction(false)}
                      className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted"
                    >
                      {t("common.cancel")}
                    </button>
                  </div>
                </div>
              )}

              <div className="mt-3">
                <LeadInteractionTimeline interactions={lead.interactions} />
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
}
